import type React from 'react'
import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { toCanvasPoint } from '@/components/app/editor/tools/shared/helpers/svg-point'
import { computeCropFrameResize } from '@/lib/photos/crop-frame-resize'
import { visualInsetRatiosForShape } from '@/lib/shapes/shape-visual-bounds'
import { resolveDragRotationDegrees } from '../helpers/drag-rotation'
import { computePannedPhotoFill } from '../helpers/photo-pan'
import type { CropOverlayProps } from '../types/crop-overlay-props'
import type { RotateDrag, ShapeRotateDrag } from '../types/crop-drag'
import type { CropDragState } from './use-crop-drag-state'

type CropChangeCallbacks = Pick<
  CropOverlayProps,
  | 'onFrameChange'
  | 'onFrameCommit'
  | 'onPhotoPanChange'
  | 'onPhotoPanCommit'
  | 'onRotateChange'
  | 'onRotateCommit'
  | 'onShapeRotateChange'
  | 'onShapeRotateCommit'
>

interface UseCropDragMoveOptions extends CropChangeCallbacks {
  state: CropDragState
  shape: ShapeData
  bleedWidth: number
  bleedHeight: number
}

/** The photo fill a photo-rotate drag produces at this pointer position. */
function rotatedPhotoFill(drag: RotateDrag, e: React.PointerEvent): ShapePhotoFill {
  const degrees = resolveDragRotationDegrees(
    drag,
    drag.initialPhotoFill.rotation ?? 0,
    e.clientX,
    e.clientY,
    e.shiftKey
  )

  return { ...drag.initialPhotoFill, rotation: degrees }
}

/** The shape rotation a shape-rotate drag produces at this pointer position. */
function shapeRotationDegrees(drag: ShapeRotateDrag, e: React.PointerEvent): number {
  return resolveDragRotationDegrees(drag, drag.initialRotation, e.clientX, e.clientY, e.shiftKey)
}

/** Pointer move/up handlers shared by every crop gesture: live updates while dragging, then the
 *  matching commit on release. */
export function useCropDragMove({
  state,
  shape,
  bleedWidth,
  bleedHeight,
  onFrameChange,
  onFrameCommit,
  onPhotoPanChange,
  onPhotoPanCommit,
  onRotateChange,
  onRotateCommit,
  onShapeRotateChange,
  onShapeRotateCommit,
}: UseCropDragMoveOptions) {
  const { svgRef, dragRef, forceUpdate, geomRef, photoFillRef } = state

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current

      if (!drag) return
      e.stopPropagation()

      if (drag.type === 'rotate') {
        const nextPhotoFill = rotatedPhotoFill(drag, e)

        photoFillRef.current = nextPhotoFill
        forceUpdate((n) => n + 1)
        onRotateChange(shape.id, nextPhotoFill)
        return
      }

      if (drag.type === 'shapeRotate') {
        const degrees = shapeRotationDegrees(drag, e)

        forceUpdate((n) => n + 1)
        onShapeRotateChange(shape.id, degrees)
        return
      }

      const { x: cx, y: cy } = toCanvasPoint(
        svgRef.current,
        e.clientX,
        e.clientY,
        bleedWidth,
        bleedHeight
      )
      const dx = cx - drag.startCanvasX
      const dy = cy - drag.startCanvasY

      if (drag.type === 'resize') {
        const result = computeCropFrameResize({
          initial: { ...drag.initialGeom, rotation: shape.rotation ?? 0 },
          photoFill: drag.initialPhotoFill,
          direction: drag.handle,
          canvasDx: dx,
          canvasDy: dy,
          shiftKey: e.shiftKey,
          aspectLock: drag.aspectLock,
          minDimension: 1,
          visualInsetRatios: visualInsetRatiosForShape(shape),
        })

        geomRef.current = result.geom
        photoFillRef.current = result.photoFill
        forceUpdate((n) => n + 1)
        onFrameChange(shape.id, result.geom, result.photoFill)
        return
      }

      const initialPhotoFill = photoFillRef.current

      if (!initialPhotoFill) return

      const nextPhotoFill = computePannedPhotoFill(
        shape,
        geomRef.current,
        drag,
        initialPhotoFill,
        dx,
        dy
      )

      photoFillRef.current = nextPhotoFill
      forceUpdate((n) => n + 1)
      onPhotoPanChange(shape.id, nextPhotoFill)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      shape.id,
      shape.rotation,
      bleedWidth,
      bleedHeight,
      onFrameChange,
      onPhotoPanChange,
      onRotateChange,
      onShapeRotateChange,
    ]
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current

      if (!drag) return
      e.stopPropagation()
      dragRef.current = null

      if (drag.type === 'resize') {
        if (photoFillRef.current) {
          onFrameCommit(shape.id, geomRef.current, photoFillRef.current)
        }
      } else if (drag.type === 'rotate') {
        const nextPhotoFill = rotatedPhotoFill(drag, e)

        photoFillRef.current = nextPhotoFill
        onRotateCommit(shape.id, nextPhotoFill)
      } else if (drag.type === 'shapeRotate') {
        const degrees = shapeRotationDegrees(drag, e)

        onShapeRotateCommit(shape.id, degrees)
      } else if (photoFillRef.current) {
        onPhotoPanCommit(shape.id, photoFillRef.current)
      }

      forceUpdate((n) => n + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shape.id, onFrameCommit, onPhotoPanCommit, onRotateCommit, onShapeRotateCommit]
  )

  return { handlePointerMove, handlePointerUp }
}
